// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
// biome-ignore lint/style/useImportType: <explanation>
import * as React from 'react';
import { Box, FormControl, FormHelperText, Input, InputLabel } from './index';
import { createKeyDown, createKeyUp } from './keys';

export interface Props {
  id: string;
  value: string | number;
  autoComplete?: string;
  noTabIndex?: boolean;
  hasSubtext?: boolean;
  subtext?: string | null | undefined;
  error?: boolean;
  placeholder?: string;
  maxCharacters?: number;
  label?: string;
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  rowsMax?: number;
  type?: string;
  inputMode?: 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search';
  autoFocus?: boolean;
  numberMin?: number;
  numberMax?: number;
  disabled?: boolean;
  readOnly?: boolean;
  inputRef?: React.Ref<HTMLInputElement | HTMLTextAreaElement>;
  onFocus?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  onUpArrow?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onDownArrow?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onShiftUpArrow?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onShiftDownArrow?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onEscape?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onEscapeUp?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onEnter?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onBackspace?: (event: React.KeyboardEvent<HTMLInputElement> | React.TouchEvent) => void;
  onClick?: (
    event:
      | React.TouchEvent<HTMLInputElement | HTMLTextAreaElement>
      | React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
}
export default function TextField({
  id: idIn,
  value,
  autoComplete,
  subtext,
  hasSubtext,
  error,
  maxCharacters,
  label,
  required,
  multiline,
  rows,
  rowsMax,
  type,
  inputMode,
  placeholder,
  inputRef,
  autoFocus,
  numberMin,
  numberMax,
  disabled,
  readOnly,
  noTabIndex = false,
  onFocus,
  onBlur,
  onChange,
  onClick,
  onUpArrow,
  onDownArrow,
  onShiftUpArrow,
  onShiftDownArrow,
  onEscape,
  onEscapeUp,
  onEnter,
  onBackspace,
}: Props) {
  const onKeyDown = createKeyDown({
    onUpArrow,
    onDownArrow,
    onShiftUpArrow,
    onShiftDownArrow,
    onEscape,
    onEnter,
    onBackspace,
  });
  const onKeyUp = createKeyUp({
    onEscapeUp,
  });
  const id = `textField-${idIn}`;
  const anyError = error || (typeof value === 'string' && maxCharacters != null && value.length > maxCharacters);
  let subtextElement = null;
  if (hasSubtext) {
    subtextElement = <FormHelperText>{subtext || ''}</FormHelperText>;
  }
  let characterCounter = null;
  if (maxCharacters != null && typeof value === 'string') {
    characterCounter = (
      <FormHelperText sx={{ flex: '0 0 auto' }}>
        {value.length} / {maxCharacters}
      </FormHelperText>
    );
    if (subtextElement == null) {
      subtextElement = <div />;
    }
  }

  let subtextArea = null;
  if (subtextElement != null || characterCounter != null) {
    subtextArea = (
      <Box component="div" sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}>
        {subtextElement}
        {characterCounter}
      </Box>
    );
  }

  return (
    <FormControl
      sx={{ display: 'flex', flex: '1 1 auto', flexDirection: 'column', width: '100%' }}
      error={anyError}
      required={!!required}
    >
      {label == null ? null : (
        <InputLabel
          htmlFor={id}
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}
        >
          {label}
        </InputLabel>
      )}
      <Input
        id={id}
        value={value}
        autoComplete={autoComplete}
        disabled={disabled}
        type={type}
        multiline={!!multiline}
        rows={rows}
        maxRows={rowsMax}
        placeholder={placeholder}
        autoFocus={autoFocus}
        inputProps={{
          onKeyDown,
          onKeyUp,
          onClick,
          readOnly: readOnly ? 'readonly' : undefined,
          tabIndex: noTabIndex ? -1 : undefined,
          min: type === 'number' ? numberMin : undefined,
          max: type === 'number' ? numberMax : undefined,
          inputMode,
        }}
        inputRef={inputRef}
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={onChange}
        onWheel={(event) => {
          if (inputMode === 'decimal' || inputMode === 'numeric') {
            (event.target as any).blur();
          }
        }}
      />
      {subtextArea}
    </FormControl>
  );
}
